import React, { useState, useCallback } from 'react'
import PropTypes from 'prop-types'
import { useNavigate } from 'react-router-dom'

import {
  FormattedMessage,
  injectIntl,
} from 'react-intl'

import AppDispatcher from '../../../dispatchers/AppDispatcher'

import {
  createCustomer,
  createRelation,
} from '../../../actions/actions'

import {
  inviteUser,
} from '../../../actions/user'

import {
  CapitalButton,
} from '../Navigation/Buttons'

import {
  TextInput,
  SelectInput,
} from '../Inputs/Inputs'

import AppStore from '../../../stores/AppStore'

import styles from '../../../../css/home.module.css'
import settingStyles from '../../../../css/settings.module.css'

function AddCustomerStep2 ({
  intl: { formatMessage, locale },
  email,
  previousPage,
  step2Callback,
  onSubmit,
}) {
  const navigate = useNavigate()

  const [language, setLanguage] = useState(locale)
  const [invalid, setInvalid] = useState(true)
  const [saved, setSaved] = useState(false)
  const [newCompany, setNewCompany] = useState('')
  const [existingCompany, setExistingCompany] = useState('')
  const [selectedCustomer, setSelectedCustomer] = useState('')
  const [inviteError, setInviteError] = useState(false)

  const handleChange = useCallback((field, value) => {
    if (field === 'language') setLanguage(value)
    if (field === 'invalid') setInvalid(value)
    if (field === 'saved') setSaved(value)
    if (field === 'newCompany') {
      setNewCompany(value)
      setExistingCompany((prev) => {
        setInvalid(prev !== 'Create Customer' || value === '')
        return prev
      })
    }
    if (field === 'existingCompany') {
      setExistingCompany(value)
      setNewCompany((prev) => {
        setInvalid(value === '' || (value === 'Create Customer' && prev === ''))
        return prev
      })
    }
  }, [])

  const childChange = useCallback((event) => {
    handleChange(event.target.name, event.target.value)
  }, [handleChange])

  const saveButtonFunction = useCallback(() => {
    const brand_name = AppStore.getCustomerBrand().name
    const distributor_id = `${AppStore.getCustomer().id}`

    if (existingCompany === 'Create Customer') {
      createCustomer(
        {
          customer_name: newCompany,
          brand_name,
          parent_id: distributor_id,
          relation: 'Distributor',
          groups_enabled: true,
          is_active: true,
          is_distributor: false,
          security: {},
        },
        (error, data) => {
          if (error) {
            setSaved('error')
          } else {
            inviteUser(
              {
                customer_id: data[0].id,
                email,
                brand_name,
                permissions: { level: 20 },
              },
              (inviteError) => {
                if (inviteError) {
                  setSaved('error')
                  setInviteError(true)
                  inviteNotSent()
                } else {
                  setSaved(true)
                  setSelectedCustomer(data[0].id)
                  setTimeout(() => {
                    isFinished()
                  }, 500)
                }
              }
            )
          }
        }
      )
    } else {
      createRelation(
        {
          customer_id: distributor_id,
          child_id: existingCompany,
          relation: 'Distributor',
        },
        (error) => {
          if (error) {
            setSaved('error')
          } else {
            setSaved(true)
            setSelectedCustomer(existingCompany)
            setTimeout(() => {
              isFinished()
            }, 500)
          }
        }
      )
    }
  }, [existingCompany, newCompany, email])

  const inviteNotSent = useCallback(() => {
    AppDispatcher.dispatch({
      action: 'get-user',
    })
  }, [])

  const isFinished = useCallback(() => {
    const { id: customerID } = AppStore.getCustomer()
    navigate(`/${customerID}/distributor/existingCustomer/${selectedCustomer}`)
  }, [navigate, selectedCustomer])

  let saveButtonStyle = `${styles.saveButton} saveButton`

  if (invalid) {
    saveButtonStyle = `${styles.saveButton} saveButton`
  } else if (saved === true) {
    saveButtonStyle = `${styles.saveButton} saveButton ${styles.saveButtonSuccess}`
  } else if (saved === 'error') {
    saveButtonStyle = `${styles.saveButton} saveButton ${styles.saveButtonError}`
  }

  // Get data from props or use empty array as fallback
  const userCustomers = []
  const SelectCustomer = formatMessage({
    id: 'customer.selectCustomer',
    defaultMessage: 'Select Customer',
    description: 'Select Customer label',
  })
  const CreateCustomer = formatMessage({
    id: 'customer.createCustomer',
    defaultMessage: 'Create Customer',
    description: 'Create Customer label',
  })

  const customerOptions = [{
    value: '',
    name: SelectCustomer,
    disabled: true,
    selected: 'selected',
  }]

  userCustomers.sort((a, b) => a.customer.customer_name.localeCompare(b.customer.customer_name))
    .map(
      (customer, idx) => customerOptions.push({
        value: customer.customer.id,
        name: customer.customer.customer_name,
      })
    )

  customerOptions.push({
    value: 'Create Customer',
    name: CreateCustomer,
  })

  return (
    <div>
      <div className={styles.userInviteInput}>
        {inviteError &&
          (
            <h5 className={`${settingStyles.inputError}`}>
              <FormattedMessage
                id='setting.warning.invite'
                defaultMessage='WARNING: A user invite to the email provided was not sent, please switch to this customer and try an invite again.'
                description='Warning Invite label'
              />
            </h5>
          )}

        <section className={styles.userInput}>
          <FormattedMessage
            id='addCustomer.associatedCustomers'
            defaultMessage='Associated Customers'
            description='Associated Customers label'
            tagName='h5'
          />
          <div className={styles.userInputValueEdit}>
            <SelectInput
              options={customerOptions}
              childChange={childChange}
              field='existingCompany'
            />
          </div>
        </section>
        {existingCompany === 'Create Customer' &&
        (
          <section className={styles.userInput}>
            <FormattedMessage
              id='addCustomer.companyEmail'
              defaultMessage='New Customer'
              description='Company label'
              tagName='h5'
            />
            <div className={styles.userInputValueEdit}>
              <TextInput
                value={newCompany}
                childChange={childChange}
                field='newCompany'
              />
            </div>
          </section>
        )}
      </div>

      <CapitalButton
        text={formatMessage({
          id: 'customer.nextButton',
          defaultMessage: 'Next',
          description: '',
        })}
        buttonFunction={saveButtonFunction}
        hSize='h2'
        style={saveButtonStyle}
        disabled={invalid}
      />
    </div>
  )
}

AddCustomerStep2.propTypes = {
  intl: PropTypes.shape({
    formatMessage: PropTypes.func,
    locale: PropTypes.string,
  }),
  email: PropTypes.string,
  previousPage: PropTypes.func,
  step2Callback: PropTypes.func,
  onSubmit: PropTypes.func,
}

export default injectIntl(AddCustomerStep2)
